/**
 * AssessmentPipeline Component
 * Displays 6 assessment stages in a horizontal pipeline with counts
 */

import { ClipboardList, FilePlus, Sparkles, ListChecks, CheckCheck, Radar } from "lucide-react";
import { Skeleton } from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ROUTES } from "@/shared/constants/routes";
import { PipelineStage, type StageType } from "./PipelineStage";
import { SectionError } from "./SectionError";
import { useGetAssessmentStages } from "../requests";
import type { AssessmentStagesResponse } from "../types";

// Stage configuration with labels and icons
const stageConfig: Record<StageType, { label: string; icon: typeof ClipboardList }> = {
  intake_form: { label: "Intake Form", icon: ClipboardList },
  information_gathering: { label: "Information Gathering", icon: FilePlus },
  generating_results: { label: "Generating Results", icon: Sparkles },
  review_results: { label: "Review Results", icon: ListChecks },
  completed: { label: "Completed", icon: CheckCheck },
  continuous_monitoring: { label: "Continuous Monitoring", icon: Radar },
};

// Order of stages in the pipeline
const stageOrder: StageType[] = [
  "intake_form",
  "information_gathering",
  "generating_results",
  "review_results",
  "completed",
  "continuous_monitoring",
];

// Map stage types to vendor-v2 filter values
const stageToFilterMap: Record<StageType, string> = {
  intake_form: "intake_form",
  information_gathering: "information_gathering",
  generating_results: "generating_results",
  review_results: "needs_review",
  completed: "complete",
  continuous_monitoring: "continuous_monitoring",
};

/**
 * Loading skeleton for assessment pipeline
 */
const AssessmentPipelineSkeleton = () => (
  <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg">
    <Skeleton className="h-5 w-32 rounded self-start" />
    <div className="flex flex-row items-center py-4 w-full">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="flex flex-col items-center gap-4 flex-1">
          <div className="flex flex-row items-center w-full">
            <div className="flex-1 h-0 border-t border-gray-200" />
            <Skeleton className="w-12 h-12 rounded-full" />
            <div className="flex-1 h-0 border-t border-gray-200" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-4 h-4 rounded" />
            <Skeleton className="w-20 h-4 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Get count for a stage from API response
 */
function getStageCount(stages: AssessmentStagesResponse["stages"], stageType: StageType): number {
  const stage = stages.find((s) => s.assessment_stage === stageType);
  return stage?.count ?? 0;
}

export const AssessmentPipeline = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetAssessmentStages();

  const handleStageClick = useCallback(
    (stageType: StageType) => {
      const filterValue = stageToFilterMap[stageType];
      navigate(`/${ROUTES.THIRD_PARTY_VENDORS_V2}?assessment_status=${filterValue}`);
    },
    [navigate],
  );

  // Loading state
  if (isLoading) {
    return <AssessmentPipelineSkeleton />;
  }

  // Error state - wrap in container with title
  if (isError || !data) {
    return (
      <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg">
        <h3 className="w-full font-inter font-medium text-sm leading-5 text-[#0F172B]">Assessment Stages</h3>
        <SectionError title="Failed to load assessment stages" onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg">
      {/* Title */}
      <h3 className="w-full font-inter font-medium text-sm leading-5 text-[#0F172B]">Assessment Stages</h3>

      {/* Pipeline Row */}
      <div className="flex flex-row items-center py-4 w-full">
        {stageOrder.map((stageType, index) => {
          const config = stageConfig[stageType];
          const count = getStageCount(data.stages, stageType);
          // continuous_monitoring is not clickable
          const isClickable = stageType !== "continuous_monitoring";

          return (
            <PipelineStage
              key={stageType}
              stageType={stageType}
              count={count}
              label={config.label}
              icon={config.icon}
              isFirst={index === 0}
              isLast={index === stageOrder.length - 1}
              onClick={isClickable ? () => handleStageClick(stageType) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export default AssessmentPipeline;
